import * as setupReducer from "containers/Setup/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as languageModelArtifactReducer from "containers/LanguageModelArtifact/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as tagsReducer from "containers/Tags/reducer";
import { delay } from "utils/promise";

import * as BlueprintAPI from "api/BlueprintAPI";
import * as AuthAPI from "api/AuthAPI";
import * as AccountAPI from "api/AccountAPI";

import * as nextReducer from "containers/Next/NextReducer";
import * as viewReducer from "containers/View/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import { toast } from "react-toastify";

import { push, replace } from "connected-react-router";

import * as PresignedUrlAPI from "api/PresignedUrlAPI";
import { constructQueryParams } from "api/BaseAPI";
import * as accountsReducer from "containers/Accounts/reducer";
import * as globalReducer from "containers/App/reducer";
import * as aiServiceReducer from "containers/AiService/reducer";
import * as fineTuningReducer from "containers/FineTuning/reducer";
import * as chatToneReducer from "containers/ChatTone/reducer";
import { CHATAIBLE_TONES } from "containers/ChatTone/reducer";
import { AIBLE_INTERN_LABEL_PREFIX } from "containers/ChatGPT/reducer";

import { getAPIToken } from "utils/request";

import moment from "moment";
import { getChatMessageDetails } from "containers/ChatDetails/reducer";
import * as folderReducer from "../Folders/reducer";
import { builderFromDataset } from "@/utils/relation";
import * as relationReducer from "@/containers/Relation/reducer";

const UPDATE_FIELD = "chatApp/UPDATE_FIELD";
const RESET = "chatApp/RESET";

export const CHATAPP_ROLE = foldersReducer.ROLE_VALUES?.OWNER?.value ?? "OWNER";

export const CHATAPP_IMAGE_SIZE = 512000;

const initialState = {
  allowedAppUseCases: undefined,
  allowedAppDatasets: undefined,
  startInDashboard: undefined,
  appAZAllocation: 0
};
/*
http://localhost:3000/next/new/new/3406?ui-state=eyJ1cmwiOiJodHRwczovL2FwaS1kZXYuYWlwbHVzaS5jb20vdjEvYWNjb3VudC8yNTUwL2ZvcmVpZ25fc3RzP3gtYWlibGUtdXNlci1pZD0xOCZ4LWFpYmxlLXRlbmFudC1pZD0yJngtYWlibGUtYWN0aW9uPWFjY291bnQuY3JlYXRlX2ZvcmVpZ25fc3RzX3Rva2VuJngtYWlibGUtYXV0aC1leHBpcmVzLWF0PTIwMjMtMTItMjFUMjIlM0E1NyUzQTEzLjA3NjQ4NCZ4LWFpYmxlLWF1dGhvcml6YXRpb249OTRkYjgxMmRmY2UxNDJlOThmNGM1NzEwNDQxYjM1MGUmeC1haWJsZS1tZXRob2Q9Z2V0JngtYWlibGUtaGFzaD1za2lfZ1ZncG8ta0ZZNmxKeDVPV1hpcnM4SnRmdU9abVZsT3RMU19MUE1BJTNEIiwibWV0aG9kIjoiR0VUIiwibGluayI6Imh0dHA6Ly9sb2NhbGhvc3Q6MzAwMC9uZXh0L25ldy9uZXcvMzQwNiIsIm5hbWUiOiJjaGF0IGFwcCBidWciLCJhY2NvdW50X2lkIjoyNTUwLCJlbWFpbCI6InRlc3RAYWlibGUuY29tIiwiaWQiOjM0MDYsInR5cGUiOiJTSEFSRV9MSU5LIn0%3D
*/
function chatAppReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case RESET:
      return initialState;
    default:
      return state;
  }
}

// Unlike usecases, we save the entire chatapp, but these are the keys that we check lineage on
// we still need to modify the get method to use lineage, but for saving, we only save the values if
// it is different than the parent chatapp
const CHATAPP_KEYS_LINEAGE = [
  {
    key: "steps",
    values: ["chatBlueprintId", "datasetId", "artifactId"]
  },
  "allowedAppDatasets",
  "allowedAppDocuments",
  "allowedAppUseCases",

  "chatAppSaveFewShot",

  "chatTone",

  "datasetId",

  "modelId",
  "frequencyPenalty",
  "maxTokens",
  "temperature",

  "retrievalK",
  "retrieverSearchType",

  "secondaryModelId",
  "datasetColumnDetails",

  "detectValuesLLM",
  "totalChartLimit",
  "includeChartCounts",
  "chatRestrictChartsToPinned"
];

// The `type` of change indicates how we label the value of if to display
//    * `VALUE` means we just use the raw value
//    * the other options means we look up the name of the item in `getExternalName`
// In the `keys`, an object means we look at the type and determine behavior from there,
//    * array means we check values in the arrays to make sure there's no differences
//    * object means we get the object at the key and then we iterate through the subKeys into the child objects until we arrive at the target value
const CHATAPP_LINEAGE_CHANGES = [
  {
    value: "NAME",
    label: "Name",
    keys: ["name"],
    type: "VALUE"
  },
  {
    value: "DESCRIPTION",
    label: "Description",
    keys: ["description"],
    type: "VALUE"
  },
  {
    value: "BRANDING",
    label: "Branding",
    keys: [
      "backgroundColor",
      "backgroundColorDarkMode",
      "base64StringBot",
      "base64StringLogo",
      "chatButtonColor",
      "chatButtonCompany",
      "chatButtonDarkMode",
      "headerColor",
      "headerDarkMode",
      "linkColor",
      "linkColorDarkMode",
      { key: "steps", subKeys: ["0", "initialChatText"], type: "object" }
    ]
  },
  {
    value: "TARGET_DATASET",
    label: "Dataset",
    keys: ["datasetId", { key: "steps", subKeys: ["0", "datasetId"], type: "object" }],
    type: "DATASET"
  },
  // Temporarily removed until I figure out a way to do this without duplicating a single change in target dataset
  // add a key pointing back to `TARGET_DATASET` so the code knows to look at it and remove duplicates?
  // {
  //   value: "ALLOWED_DATASETS",
  //   label: "Dataset Options",
  //   keys: [
  //     { key: "allowedAppDatasets", type: "array" },
  //   ],
  //   type: "DATASET",
  // },
  {
    value: "TARGET_DOCUMENT",
    label: "Document Set",
    keys: [{ key: "steps", subKeys: ["0", "artifactId"], type: "object" }],
    type: "DOCUMENT"
  },
  // {
  //   value: "ALLOWED_DOCUMENTS",
  //   label: "Document Set Options",
  //   keys: [
  //     { key: "allowedAppDocuments", type: "array" },
  //   ],
  //   type: "DOCUMENT"
  // },
  {
    value: "TARGET_USECASE",
    label: "Agent Template",
    keys: [{ key: "steps", subKeys: ["0", "chatBlueprintId"], type: "object" }],
    type: "BLUEPRINT"
  },
  // {
  //   value: "ALLOWED_USECASES",
  //   label: "Use Case Options",
  //   keys: [
  //     { key: "allowedAppUseCases", type: "array" },
  //   ],
  //   type: "DOCUMENT"
  // },
  {
    value: "MODEL_ID",
    label: "AI Model",
    keys: ["modelId"],
    type: "MODEL"
  },
  {
    value: "MODEL_SETTINGS",
    label: "AI Model Configuration",
    keys: ["frequecyPenalty", "maxTokens", "temperature"]
  },
  {
    value: "CHAT_TONE",
    label: "Chat Tone",
    keys: ["chatTone"],
    type: "CHAT_TONE"
  },
  {
    value: "DOCUMENTSET_SETTINGS",
    label: "Document Set Settings",
    keys: ["retrievalK", "retrieverSearchType"]
  },
  {
    value: "SQL_DATASET_DETAILS",
    label: "Dataset Column Descriptions",
    keys: ["datasetColumnDetails"]
  },
  {
    value: "SQL_MODEL_ID",
    label: "SQL Generation Model",
    keys: ["secondaryModelId"],
    type: "MODEL"
  },
  {
    value: "ANALYTICS_CHARTS",
    label: "Chart Settings",
    keys: ["detectValuesLLM", "totalChartLimit", "includeChartCounts", "chatRestrictChartsToPinned"]
  }
];

/* Selectors */
export function getBase(state) {
  return state.chatApp;
}

export function getIsCreatingApp(state) {
  return getBase(state)?.isCreatingApp;
}

export function getSteps(state) {
  return getBase(state)?.steps ?? { [0]: { chatBlueprintId: "CUSTOM_PROMPT" } };
}

export function getStepIndex(state) {
  return getBase(state)?.stepIndex ?? 0;
}

export function getCurrentStep(state) {
  const steps = getSteps(state);
  const stepIndex = getStepIndex(state);

  return steps?.[stepIndex];
}

export function getAppName(state) {
  return getBase(state)?.appName;
}

export function getAppDescription(state) {
  return getBase(state)?.appDescription;
}

export function getAppCompany(state) {
  return getBase(state)?.appCompany;
}

export function getHideOutcomeSelection(state) {
  return getBase(state)?.hideOutcomeSelection;
}

export function getIsKPIAnalysis(state, datasetId) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const savedIsKPIAnalysis = chatApp?.wizard?.isKPIAnalysis;
  const localIsKPIAnalysis = getBase(state)?.isKPIAnalysis;
  if (localIsKPIAnalysis !== undefined) {
    return localIsKPIAnalysis;
  }

  // Passing a datasetOverride in sense view to get saved dataset value
  if (datasetId !== undefined) {
    const datasetIsKpiAnalysis = homeReducer.getIsKPIAnalysis(state, datasetId);
    return datasetIsKpiAnalysis;
  }

  return savedIsKPIAnalysis;
  return getBase(state)?.isKPIAnalysis;
}

export function addTrainingDatasetToChatApp(id, example_set_id) {
  return async (dispatch, getState) => {
    if (!id || isNaN(id)) {
      throw new Error("Invalid ChatApp");
    }
    if (!example_set_id || isNaN(example_set_id)) {
      throw new Error("Invalid Dataset");
    }
    try {
      await dispatch(blueprintReducer.fetchBlueprint(id));
    } catch (e) {
      console.log(e);
    }

    const currBP = blueprintReducer.getBlueprint(getState(), id);
    if (!currBP || currBP?.wizard?.isAZTestApp || (currBP && currBP.wizard && !currBP.wizard.isChatApp)) {
      return Promise.resolve();
    }

    const currTrainingDatasets = currBP?.wizard?.exampleSetsCreated ?? [];

    if (!currTrainingDatasets.includes(example_set_id)) {
      currTrainingDatasets.push(Number(example_set_id));

      const newWizard = {
        ...currBP.wizard,
        exampleSetsCreated: currTrainingDatasets
      };

      return BlueprintAPI.updateBlueprint(id, newWizard).then((response) => {
        dispatch(blueprintReducer.updateBlueprint(response.id, response));
      });
    }

    return Promise.resolve();
  };
}

export function removeTrainingDatasetFromChatApp(id, example_set_id) {
  return async (dispatch, getState) => {
    if (!id || isNaN(id)) {
      throw new Error("Invalid ChatApp");
    }
    if (!example_set_id || isNaN(example_set_id)) {
      throw new Error("Invalid Dataset");
    }
    try {
      await dispatch(blueprintReducer.fetchBlueprint(id));
    } catch (e) {
      console.log(e);
    }

    const currBP = blueprintReducer.getBlueprint(getState(), id);
    if (!currBP || currBP?.wizard?.isAZTestApp || (currBP && currBP.wizard && !currBP.wizard.isChatApp)) {
      return Promise.resolve();
    }

    const currTrainingDatasets = currBP?.wizard?.exampleSetsCreated ?? [];

    if (currTrainingDatasets.includes(example_set_id)) {
      const newExampleSets = currTrainingDatasets.filter((bpId) => {
        return bpId !== example_set_id;
      });

      const newWizard = {
        ...currBP.wizard,
        exampleSetsCreated: newExampleSets
      };

      return BlueprintAPI.updateBlueprint(id, newWizard).then((response) => {
        dispatch(blueprintReducer.updateBlueprint(response.id, response));
      });
    }

    return Promise.resolve();
  };
}

export function createAZTestForIntern({
  appId,
  appName,
  modelId,
  selectedTest,
  newTestName,
  newTestDistribution
} = {}) {
  return async (dispatch, getState) => {
    const currBlueprint = blueprintReducer.getBlueprint(getState(), appId);
    const retWizard = {
      ...currBlueprint?.wizard,
      name: appName,
      modelId: modelId
    };

    let exampleSetId = undefined;
    const retModel = chatGPTReducer.getAiServiceModel(getState(), modelId);
    const fineTuningExecutionId = retModel?.fine_tuning_execution_id;
    if (fineTuningExecutionId) {
      const fineTuningExecution = fineTuningReducer.getFineTuningById(getState(), fineTuningExecutionId);
      exampleSetId = fineTuningExecution?.example_set_id;
    }

    if (exampleSetId && retWizard?.exampleSetsCreated) {
      retWizard.exampleSetsCreated = retWizard.exampleSetsCreated.filter((eId) => eId !== exampleSetId);
    }

    const newApp = await dispatch(blueprintReducer.saveBlueprintToAPI(appName, retWizard));

    let testWizard = {
      azTests: [],
      isAZTestApp: true,
      isChatApp: true,
      name: newTestName
    };
    if (selectedTest) {
      await dispatch(blueprintReducer.fetchBlueprint(selectedTest));
      const testBp = blueprintReducer.getBlueprint(getState(), selectedTest);
      testWizard = { ...testBp?.wizard };
    }

    const newDist = {
      [newApp.id]: newTestDistribution["NEW_CHAT_APP"],
      ...newTestDistribution
    };
    delete newDist["NEW_CHAT_APP"];

    const newApps = Object.keys(newDist).map((k) => (isNaN(k) ? k : Number(k)));

    const newTest = {
      appDistribution: newDist,
      dateStart: new Date().toISOString(),
      testApps: newApps
    };
    testWizard.azTests.push(newTest);

    let newTestApp;
    if (selectedTest) {
      newTestApp = await BlueprintAPI.updateBlueprint(selectedTest, testWizard).then((response) => {
        dispatch(blueprintReducer.updateBlueprint(response.id, response));
      });
    } else {
      newTestApp = await dispatch(blueprintReducer.saveBlueprintToAPI(newTestName, testWizard));
    }

    if (exampleSetId) {
      await dispatch(removeTrainingDatasetFromChatApp(appId, exampleSetId));
    }

    return newTestApp;
  };
}

// given a chatapp, returns the id of the associated az test
export function getChatAppAZTest(state, id) {
  const azTestApps = getActiveAZTests(state);
  const parentTests = azTestApps.filter((bp) => {
    const currTest = getCurrentAZTest(state, bp?.id);
    const testApps = currTest?.testApps ?? [];
    return testApps.find((tId) => {
      return tId === id;
    });
  });
  return parentTests[parentTests.length - 1]?.id;
}

// Returns the modelId of the option that is the updated version of the chatapp model
// Returns undefined if there's no update;
export function getChatAppFineTunedModel(state, id) {
  const chatApp = blueprintReducer.getBlueprint(state, id);
  const exampleSets = chatApp?.wizard?.exampleSetsCreated;

  if (!exampleSets?.length) {
    return undefined;
  }

  const fineTuningExecutions = fineTuningReducer.getFineTunings(state);

  const validExecs = {};

  Object.keys(fineTuningExecutions).forEach((fKey) => {
    const currExec = fineTuningExecutions[fKey];
    if (exampleSets.includes(currExec.example_set_id)) {
      validExecs[fKey] = currExec.example_set_id;
    }
  });

  let latestModel = undefined;
  const validModels = [];

  Object.values(aiServiceReducer.getModels(state)).forEach((m) => {
    const mExec = m.fine_tuning_execution_id;
    if (validExecs[mExec]) {
      // ModelId as defined in `getModelOptions` in chatGPTReducer
      latestModel = m.external_id;
      validModels.push(m);
    }
  });

  return latestModel;
}

// Check if there is a new fine tuned model for the chatapp
export function getHasNewFineTunedModel(state, id) {
  const app = blueprintReducer.getBlueprint(state, id);

  if (app?.wizard?.isAZTestApp) {
    // No badges on existing az tests
    return false;
    // If it's an a/z test, check the child apps
    // const currTest = getCurrentAZTest(state, id);
    // const hasNewModel = currTest?.testApps?.find(aId => getHasNewFineTunedModel(state, aId));
    // return Boolean(hasNewModel);
  } else if (app?.wizard?.isChatApp) {
    // get the fine tuned model for the app and return true if that exists
    return Boolean(getChatAppFineTunedModel(state, id));
  }

  // for ids that aren't chatapps or az tests
  return false;
}

export function getAddToAzTest(state) {
  return getBase(state)?.addToAzTest;
}

export function getAppAZAllocation(state) {
  return getBase(state)?.appAZAllocation;
}

export function getAppAZDistribution(state) {
  return getBase(state)?.appAZDistribution ?? {};
}

export function getAZTests(state, id) {
  const chatApp = blueprintReducer.getBlueprint(state, id);
  const tests = chatApp?.wizard?.azTests;
  return tests;
}

export function getCurrentAZTest(state, id) {
  const tests = getAZTests(state, id) ?? [];
  const currTest = tests[tests.length - 1];
  return currTest;
}

export function generateAZTestSeed() {
  // seed is a number from 0 to 99.99
  const seed = Number((100 * Math.random()).toFixed(2));
  return seed;
}

export function getAZTestID(state, id, currTestOverride, seedOverride) {
  const currTest = currTestOverride ?? getCurrentAZTest(state, id);

  const testApps = currTest?.testApps ?? [];
  const appDistribution = currTest.appDistribution;

  const seed = seedOverride ?? chatGPTReducer.getAZTestSeed(state);

  let currCheck = 0;
  let retAppId = testApps[0];
  testApps.forEach((aId) => {
    const minCheck = currCheck;
    currCheck += Number(appDistribution[aId] ?? 0);
    const maxCheck = currCheck;
    if (seed >= minCheck && seed < maxCheck) {
      retAppId = aId;
    }
  });

  return retAppId;
}

export function setAzTestSeed() {
  return (dispatch, getState) => {
    const currSeed = chatGPTReducer.getAZTestSeed(getState());
    if (isNaN(currSeed)) {
      const newSeed = generateAZTestSeed();
      dispatch(chatGPTReducer.updateAZTestSeed(newSeed));
    }
  };
}

export function getAZTestOptions(state, id) {
  const currTest = getCurrentAZTest(state, id);

  const testApps = currTest?.testApps ?? [];
  const appDistribution = currTest?.appDistribution;

  let currVal = 0;

  const options = testApps.map((tId) => {
    const app = blueprintReducer.getBlueprint(state, tId);
    const appName = app?.name;

    const currMin = currVal;
    const currDist = appDistribution?.[tId] || 0;
    currVal += currDist;
    const currMax = currVal;
    const val = (currMax + currMin) / 2;

    return {
      label: appName,
      value: val,
      min: currMin,
      max: currMax,
      id: tId
    };
  });

  return options;
}

export function selectAZTestApp(val) {
  return async (dispatch, getState) => {
    dispatch(chatGPTReducer.updateAZTestSeed(val));

    const chatApp = chatGPTReducer.getChatApp(getState());

    return dispatch(chatGPTReducer.initChatAppHelper(chatApp?.id, true));
  };
}

export function fetchAZTestApp(id, checkedIds = {}) {
  return async (dispatch, getState) => {
    let chatApp = blueprintReducer.getBlueprint(getState(), id);
    if (chatApp === undefined) {
      await dispatch(blueprintReducer.fetchBlueprint(id));
    }
    const isValidChatApp = chatApp?.wizard?.isChatApp === true;
    const isAZTestApp = chatApp?.wizard?.isAZTestApp === true;

    if (!isValidChatApp || !isAZTestApp || checkedIds?.[id]) {
      return chatApp;
    }

    // generate a new seed each session
    dispatch(setAzTestSeed());
    const appId = getAZTestID(getState(), id);

    let testApp = await dispatch(fetchAZTestApp(appId, { ...checkedIds, [id]: true }));
    if (!testApp) {
      await dispatch(blueprintReducer.fetchBlueprint(appId));
      testApp = blueprintReducer.getBlueprint(getState(), appId);
    }

    const currTest = getCurrentAZTest(getState(), id);
    const allTestApps = currTest?.testApps ?? [];

    let allAppsChecked = { ...checkedIds };
    allTestApps.forEach((aId) => {
      allAppsChecked[aId] = true;
      const currApp = blueprintReducer.getBlueprint(getState(), aId);
      if (!currApp) {
        dispatch(fetchAZTestApp(aId, allAppsChecked));
      }
    });
    chatApp = {
      ...chatApp,
      wizard: {
        ...chatApp.wizard,
        ...testApp?.wizard,
        testAppDetails: {
          ...testApp,
          wizard: undefined
        }
      }
    };

    return chatApp;
  };
}

export function getAZTestAppOptions(state, id) {
  const currTest = getCurrentAZTest(state, id);

  const testApps = currTest?.testApps ?? [];
  const appDistribution = currTest.appDistribution;

  let currCheck = 100;
  const options = testApps.map((appId) => {
    const cBp = blueprintReducer.getBlueprint(state, appId);
    const cName = cBp?.name;

    const oldCheck = currCheck;
    const newCheck = currCheck - Number(appDistribution[appId] ?? 0);
    currCheck = newCheck;

    const appVal = (newCheck + oldCheck) / 2;

    const cOption = {
      label: cBp?.name,
      value: appVal,
      min: newCheck,
      max: oldCheck
    };

    return cOption;
  });

  return options;
}

export function getActiveAZTests(state) {
  const blueprints = blueprintReducer.getBlueprints(state);
  const sFolder = folderReducer.getSelectedFolder(state);

  return blueprints
    .filter((bp) => {
      return bp?.wizard?.isChatApp && bp?.wizard?.isAZTestApp;
    })
    .filter((d) => d.folder_id === sFolder);
}

function getIsReportAZTest(state, chatAppId, reportId, testIndexOverride, reportOverride) {
  const azTests = getAZTests(state, chatAppId) ?? [];
  const testIndex = testIndexOverride ?? azTests.length - 1;
  const currTest = azTests[testIndex];
  const currApps = currTest.testApps;
  const dateStart = new Date(currTest.dateStart);
  const dateEnd = currTest.dateEnd ? new Date(currTest.dateEnd) : undefined;

  const report = reportOverride ?? reportReducer.getReport(state, reportId);

  if (Number(report?.metadata?.chatGPTConvo?.chatApp?.id) !== Number(chatAppId)) {
    return false;
  }

  const reportDate = new Date(report?.createdDate);

  if (reportDate < dateStart) {
    return false;
  }

  if (dateEnd && reportDate >= dateEnd) {
    return false;
  }

  return true;
}

export function getAZTestTrainingData(state, id, testIndexOverride) {
  const blueprint = blueprintReducer.getBlueprint(state, id);
  const azTests = getAZTests(state, id) ?? [];
  const testIndex = testIndexOverride ?? azTests.length - 1;
  const currTest = azTests[testIndex];
  const currApps = currTest.testApps;

  const trainingData = {};

  currApps.forEach((aId) => {
    const blueprint = blueprintReducer.getBlueprint(state, aId);
    const addTrainingDataset = blueprint?.wizard?.addTrainingDataset;
    if (addTrainingDataset) {
      const trainingDatasetId = blueprint?.wizard?.trainingDatasetId;
      const trainingDataset = languageModelArtifactReducer.getFineTuningData(state, trainingDatasetId);
      const recordCount = trainingDataset?.metadata?.record_count;

      if (trainingDataset) {
        const retData = {
          trainingDatasetId: trainingDatasetId,
          records: recordCount
        };
        trainingData[aId] = retData;
      }
    }
  });

  return trainingData;
}

export function getAZTestConcurrencyValues(state, id, testIndexOverride) {
  const GPU_COST = 2.16;
  // Table defining the different concurrency levels for each input/output level
  /*
    ROW DATA:
    [
      [ input_tokens, output_tokens ],
      [ [HIGH_concurrency, HIGH_latency], [MED_concurrency, MED_latency], [NONE_concurrency, NONE_latency] ]
    ]
  */
  const RAW_CONFIG = [
    [
      [20000, 2000],
      [
        [10, 170],
        [5, 100],
        [1, 50]
      ]
    ],
    [
      [8500, 350],
      [
        [16, 21],
        [4, 8],
        [1, 6]
      ]
    ],
    [
      [5000, 500],
      [
        [25, 23],
        [10, 13],
        [1, 7]
      ]
    ],
    [
      [2800, 350],
      [
        [32, 14],
        [8, 6],
        [1, 5]
      ]
    ],
    [
      [1500, 350],
      [
        [48, 12],
        [16, 7],
        [1, 4]
      ]
    ],
    [
      [1000, 1000],
      [
        [50, 24],
        [25, 17],
        [1, 12]
      ]
    ],
    [
      [500, 2000],
      [
        [50, 43],
        [25, 33],
        [1, 25]
      ]
    ],
    [
      [350, 250],
      [
        [64, 7],
        [32, 6],
        [1, 4]
      ]
    ]
  ];

  const CONCURRENCY_ORDER = ["HIGH", "MEDIUM", "NONE"];

  const CONCURRENCY_CONFIG = RAW_CONFIG.map((cRow) => {
    const tokenData = cRow[0];
    const concurrencyDistribution = cRow[1];

    const retValues = {};
    CONCURRENCY_ORDER.forEach((cKey, index) => {
      const currDist = concurrencyDistribution[index];
      const currConcurrency = currDist[0];
      const currLatency = currDist[1];
      const currCost = (GPU_COST * currLatency) / 3600;
      const currValue = {
        concurrency: currConcurrency,
        latency: currLatency,
        cost: currCost
      };
      retValues[cKey] = currValue;
    });

    const retEntry = {
      input_tokens: tokenData[0],
      output_tokens: tokenData[1],
      values: retValues
    };

    return retEntry;
  });

  CONCURRENCY_CONFIG.sort((a, b) => {
    if (a.output_tokens === b.output_tokens) {
      return b.input_tokens - b.output_tokens;
    }
    return b.output_tokens - a.output_tokens;
  });

  let retInputTokens = 0;
  let retOutputTokens = 0;

  const testProjects = reportReducer.getReportsArr(state).filter((r) => {
    return getIsReportAZTest(state, id, r?.id, testIndexOverride, r);
  });

  testProjects.forEach((report) => {
    const conversation = report?.metadata?.chatGPTConvo?.conversation ?? {};
    const validMessages = Object.values(conversation).filter((message) => {
      return message?.contentGenerationIds?.length;
    });
    validMessages.forEach((message) => {
      if (message?.contentGenerationIds?.length) {
        message.contentGenerationIds.forEach((mId) => {
          const messageDetails = getChatMessageDetails(state, mId);
          const usage = messageDetails?.data?.usage;
          retInputTokens = Math.max(retInputTokens, usage?.input_token_count || 0);
          retOutputTokens = Math.max(retOutputTokens, usage?.output_token_count || 0);
        });
      }
    });
  });

  if (!retInputTokens && !retOutputTokens) {
    retInputTokens = 1000;
    retOutputTokens = 500;
  }

  let retConfig = CONCURRENCY_CONFIG.reduce((acc, conf) => {
    if (!acc) {
      return conf;
    }
    if (retInputTokens <= conf.input_tokens && retOutputTokens <= conf.output_tokens) {
      return conf;
    }
    return acc;
  }, undefined);

  return retConfig;
}

export function getAZTestAppReport(state, id, testIndexOverride) {
  const blueprint = blueprintReducer.getBlueprint(state, id);
  const azTests = getAZTests(state, id) ?? [];
  const testIndex = testIndexOverride ?? azTests.length - 1;
  const currTest = azTests[testIndex];
  const currApps = currTest.testApps;
  const dateStart = new Date(currTest.dateStart);
  const dateEnd = currTest.dateEnd ? new Date(currTest.dateEnd) : undefined;

  const testProjects = reportReducer.getReportsArr(state).filter((r) => {
    return getIsReportAZTest(state, id, r?.id, testIndexOverride, r);
  });

  const DEFAULT_REPORT = {
    totalReports: 0,
    totalQuestions: 0,
    totalCost: 0,
    totalLikes: 0,
    totalDislikes: 0,
    answeredPrompts: 0,
    totalResponseTime: 0
  };

  const appUsage = {};
  currApps.forEach((aId) => {
    appUsage[aId] = {
      ...DEFAULT_REPORT
    };
  });

  const dailyUsage = {};

  // Evaluate each project
  testProjects.forEach((currReport) => {
    const currChat = currReport.metadata.chatGPTConvo;

    // Get the current chatapp being evaluated
    // derive the app being tested if it doesn't have the id
    let appId = currChat.chatApp.wizard?.testAppDetails?.id;
    if (!appId) {
      const seed = currChat.azTestSeed;
      appId = getAZTestID(state, id, currTest, seed);
    }

    if (!appUsage[appId]) {
      appUsage[appId] = {
        ...DEFAULT_REPORT
      };
    }
    const currObj = appUsage[appId];

    currObj.totalReports += 1;

    const conversation = currChat.conversation;
    const convArr = chatGPTReducer.getConversationArray(undefined, conversation);

    const metrics = chatGPTReducer.getChatMetrics(state, convArr);

    const questions = metrics.prompts || 0;
    const likes = metrics.totalLikes || 0;
    const dislikes = metrics.totalDislikes || 0;
    const cost = metrics.totalCost || 0;
    const answeredPrompts = metrics.answeredPrompts || 0;
    const totalResponseTime = metrics.totalResponseTime || 0;

    currObj.totalQuestions += questions;
    currObj.totalCost += cost;
    currObj.totalLikes += likes;
    currObj.totalDislikes += dislikes;
    currObj.answeredPrompts += answeredPrompts;
    currObj.totalResponseTime += totalResponseTime;

    convArr.forEach((message) => {
      if (message?.user === "User" || message?.isQuestion) {
        const currDate = message.date;
        const cleanedDate = new Date(currDate).toLocaleDateString();
        if (!dailyUsage[cleanedDate]) {
          dailyUsage[cleanedDate] = {};
        }
        if (!dailyUsage[cleanedDate][appId]) {
          dailyUsage[cleanedDate][appId] = 0;
        }
        dailyUsage[cleanedDate][appId] += 1;
      }
    });
  });

  const dailyUsageArr = Object.keys(dailyUsage)
    .map((k) => {
      const retValue = {
        date: k
      };
      const usage = dailyUsage[k];
      currApps.forEach((uKey) => {
        retValue[uKey] = usage[uKey] ?? 0;
      });

      return retValue;
    })
    .sort((a, b) => {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);

      return aDate.getTime() - bDate.getTime();
    });

  return {
    appUsage: appUsage,
    dailyUsage: dailyUsageArr
  };
}

function getModelPrompts(state, settings, modelId) {
  const useCaseSettings = settings?.useCaseSettings;

  let retValue = useCaseSettings?.[modelId];
  if (retValue !== undefined) {
    return retValue;
  }

  const modelType = chatGPTReducer.getModelType(state, modelId);
  const modelOptions = chatGPTReducer.getSortedModelOptions(state, modelType);

  for (const option of modelOptions) {
    retValue = useCaseSettings?.[option.value];

    if (retValue !== undefined) {
      return retValue;
    }
  }

  return retValue;
}

export function getAZTestAppComparison(state, id, testIndexOverride) {
  const blueprint = blueprintReducer.getBlueprint(state, id);
  const azTests = getAZTests(state, id) ?? [];
  const testIndex = testIndexOverride ?? azTests.length - 1;
  const currTest = azTests[testIndex];
  const currApps = currTest.testApps;

  const chatTones = chatToneReducer.getToneList(state);
  const modelOptions = chatGPTReducer.getModelOptions(state);

  const comparisonResults = {};
  currApps.forEach((aId) => {
    const currObj = {};

    const currApp = blueprintReducer.getBlueprint(state, aId);

    if (!currApp?.id) {
      return;
    }

    currObj.name = currApp.name;

    const modelId = currApp.wizard?.modelId;

    let modelName = chatGPTReducer.MODEL_OPTIONS[modelId]?.label;

    if (!modelName) {
      const mOption = modelOptions.find((o) => o.value === modelId);
      if (mOption) {
        modelName = mOption.label;
      }
    }

    if (!modelName) {
      modelName = modelId;
    }

    currObj.model = {
      label: modelName,
      value: modelId
    };

    const appTone = currApp.wizard?.chatTone;
    const currTone =
      chatTones.find((t) => t?.id === appTone) ??
      CHATAIBLE_TONES[chatGPTReducer.DEFAULT_CHAT_TONE] ??
      CHATAIBLE_TONES["NEUTRAL"];

    currObj.chatTone = {
      label: currTone?.name ?? appTone,
      value: appTone
    };

    const useCaseId = currApp.wizard?.steps?.[0]?.chatBlueprintId;
    const useCase = blueprintReducer.getBlueprint(state, useCaseId);

    currObj.useCase = {
      label: useCase?.name ?? useCaseId,
      value: useCaseId
    };

    const currSettings = chatGPTReducer.getBlueprintSettings(state, useCaseId, true);
    const currPrompts = getModelPrompts(state, currSettings, modelId) ?? {};

    currObj.useCasePrompts = {
      introPrompt: currPrompts.introPrompt,
      introSystemPrompt: currPrompts.introSystemPrompt,
      systemPrompt: currPrompts.systemPrompt,
      columnsPrompt: currPrompts.columnsPrompt,
      augmentPrompt: currPrompts.augmentPrompt
    };

    comparisonResults[aId] = currObj;
  });

  const comparisonDifferences = {};

  const referenceAppId = currApps[0];
  const comparisonReference = comparisonResults[referenceAppId];
  const comparisonPrompts = comparisonReference?.useCasePrompts ?? {};

  currApps.forEach((aId) => {
    if (aId !== referenceAppId) {
      const currComparison = comparisonResults[aId];
      if (currComparison?.model?.value !== comparisonReference?.model?.value) {
        comparisonDifferences.model = true;
      }
      if (currComparison?.chatTone?.value !== comparisonReference?.chatTone?.value) {
        comparisonDifferences.chatTone = true;
      }
      if (currComparison?.useCase?.value !== comparisonReference?.useCase?.value) {
        comparisonDifferences.useCase = true;
        if (!comparisonDifferences.useCasePrompts) {
          comparisonDifferences.useCasePrompts = {};
        }
        const currPrompts = currComparison.useCasePrompts ?? {};
        if (currPrompts.introPrompt !== comparisonPrompts.introPrompt) {
          comparisonDifferences.useCasePrompts.introPrompt = true;
        }
        if (currPrompts.introSystemPrompt !== comparisonPrompts.introSystemPrompt) {
          comparisonDifferences.useCasePrompts.introSystemPrompt = true;
        }
        if (currPrompts.systemPrompt !== comparisonPrompts.systemPrompt) {
          comparisonDifferences.useCasePrompts.systemPrompt = true;
        }
        if (currPrompts.columnsPrompt !== comparisonPrompts.columnsPrompt) {
          comparisonDifferences.useCasePrompts.columnsPrompt = true;
        }
        if (currPrompts.augmentPrompt !== comparisonPrompts.augmentPrompt) {
          comparisonDifferences.useCasePrompts.augmentPrompt = true;
        }
      }
    }
  });

  return {
    differences: comparisonDifferences,
    values: comparisonResults
  };
}

export function getAZTestAppColors(state, id, testIndexOverride) {
  const AZ_TEST_COLORS = ["#20CAE8", "#E879F9", "#60A5FA", "#34D399"];
  const azTests = getAZTests(state, id) ?? [];
  const testIndex = testIndexOverride ?? azTests.length - 1;
  const currTest = azTests[testIndex];
  const currApps = currTest.testApps;

  const colors = {};

  currApps.forEach((aId, i) => {
    const cIndex = i % AZ_TEST_COLORS.length;
    colors[aId] = AZ_TEST_COLORS[cIndex];
  });

  return colors;
}

export function getAllowedAppUseCases(state) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const blueprintId = chatGPTReducer.getBlueprintId(state);
  const savedAppUseCases = chatApp?.wizard?.allowedAppUseCases;
  const currAppUseCases = getBase(state)?.allowedAppUseCases;
  let allowedAppUseCases = [blueprintId];

  if (currAppUseCases) {
    if (!currAppUseCases.includes(blueprintId)) {
      allowedAppUseCases = [...getBase(state)?.allowedAppUseCases, blueprintId];
    } else {
      allowedAppUseCases = currAppUseCases;
    }
  } else if (savedAppUseCases !== undefined) {
    if (!savedAppUseCases.includes(blueprintId)) {
      allowedAppUseCases = [...savedAppUseCases, blueprintId];
    } else {
      allowedAppUseCases = savedAppUseCases;
    }
  }

  return allowedAppUseCases.filter((bp) => bp);
}

export function getAllowedAppDatasets(state) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const nextId = nextReducer.getCurrentNextId(state);
  const datasetId = nextReducer.getDatasetId(state, nextId);

  const savedAppDatasets = chatApp?.wizard?.allowedAppDatasets;
  const currAppDatasets = getBase(state)?.allowedAppDatasets;
  let allowedAppDatasets = [datasetId];

  if (currAppDatasets) {
    if (!currAppDatasets.includes(datasetId)) {
      allowedAppDatasets = [...getBase(state)?.allowedAppDatasets, datasetId];
    } else {
      allowedAppDatasets = currAppDatasets;
    }
  } else if (savedAppDatasets !== undefined) {
    if (!savedAppDatasets.includes(datasetId)) {
      allowedAppDatasets = [...savedAppDatasets, datasetId];
    } else {
      allowedAppDatasets = savedAppDatasets;
    }
  }

  return allowedAppDatasets.filter((d) => d);
}

export function getAllowedAppDocuments(state) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const artifactId = chatGPTReducer.getArtifactId(state);

  const savedAppDocuments = chatApp?.wizard?.allowedAppDocuments;
  const currAppDocuments = getBase(state)?.allowedAppDocuments;
  let allowedAppDocuments = [artifactId];

  if (currAppDocuments) {
    if (!currAppDocuments.includes(artifactId)) {
      allowedAppDocuments = [...getBase(state)?.allowedAppDocuments, artifactId];
    } else {
      allowedAppDocuments = currAppDocuments;
    }
  } else if (savedAppDocuments !== undefined) {
    if (!savedAppDocuments.includes(artifactId)) {
      allowedAppDocuments = [...savedAppDocuments, artifactId];
    } else {
      allowedAppDocuments = savedAppDocuments;
    }
  }

  return allowedAppDocuments.filter((d) => d);
}

export function getStartInDashboard(state) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const savedStartInDashboard = chatApp?.wizard?.startInDashboard;
  const localStartInDashboard = getBase(state)?.startInDashboard;
  if (localStartInDashboard !== undefined) {
    return localStartInDashboard;
  }
  return savedStartInDashboard;
}

export function getAddTrainingDataset(state) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const savedAddTrainingDataset = chatApp?.wizard?.addTrainingDataset;
  return getBase(state)?.addTrainingDataset ?? savedAddTrainingDataset ?? false;
}

/* Actions */
export function resetChatApp() {
  return (dispatch, getState) => {
    dispatch({ type: RESET });
  };
}

export function updateIsCreatingApp(isCreatingApp) {
  return {
    type: UPDATE_FIELD,
    key: "isCreatingApp",
    data: isCreatingApp
  };
}

export function updateSteps(steps) {
  return {
    type: UPDATE_FIELD,
    key: "steps",
    data: steps
  };
}

export function updateStep(data, key) {
  return (dispatch, getState) => {
    const stepIndex = getStepIndex(getState());
    const steps = getSteps(getState());

    const retSteps = {
      ...steps,
      [stepIndex]: { ...steps[stepIndex], [key]: data }
    };

    return dispatch(updateSteps(retSteps));
  };
}

export function updateStepIndex(stepIndex) {
  return {
    type: UPDATE_FIELD,
    key: "stepIndex",
    data: stepIndex
  };
}

export function updateAppName(val) {
  return {
    type: UPDATE_FIELD,
    key: "appName",
    data: val
  };
}

export function updateAppDescription(val) {
  return {
    type: UPDATE_FIELD,
    key: "appDescription",
    data: val
  };
}

export function updateAppCompany(val) {
  return {
    type: UPDATE_FIELD,
    key: "appCompany",
    data: val
  };
}

export function updateHideOutcomeSelection(val) {
  return {
    type: UPDATE_FIELD,
    key: "hideOutcomeSelection",
    data: val
  };
}

export function updateIsKPIAnalysis(val) {
  return {
    type: UPDATE_FIELD,
    key: "isKPIAnalysis",
    data: val
  };
}

export function updateStartupViewMode(val) {
  return {
    type: UPDATE_FIELD,
    key: "startupViewMode",
    data: val
  };
}

export function updateAddToAzTest(val) {
  return {
    type: UPDATE_FIELD,
    key: "addToAzTest",
    data: val
  };
}

export function updateAppAZAllocation(val) {
  return {
    type: UPDATE_FIELD,
    key: "appAZAllocation",
    data: Number(val)
  };
}

export function updateAppAZDistribution(obj) {
  return {
    type: UPDATE_FIELD,
    key: "appAZDistribution",
    data: obj
  };
}

export function updateAllowedAppUseCases(allowedAppUseCases) {
  return {
    type: UPDATE_FIELD,
    data: allowedAppUseCases,
    key: "allowedAppUseCases"
  };
}

export function updateAllowedAppDatasets(data) {
  return { type: UPDATE_FIELD, data: data, key: "allowedAppDatasets" };
}

export function updateAllowedAppDocuments(data) {
  return { type: UPDATE_FIELD, data: data, key: "allowedAppDocuments" };
}

export function updateStartInDashboard(startInDashboard) {
  return {
    type: UPDATE_FIELD,
    data: startInDashboard,
    key: "startInDashboard"
  };
}

export function updateAddTrainingDataset(addTrainingDataset) {
  return {
    type: UPDATE_FIELD,
    data: addTrainingDataset,
    key: "addTrainingDataset"
  };
}

function getChatAppLink(chatApp) {
  const origin = window.location.origin;
  const path = "/next/new/new/" + chatApp?.id;
  const url = origin + path;

  return url;
}

function generateCustomMessage(chatApp) {
  const url = getChatAppLink(chatApp);

  const message = `
  <div>
    You've been invited to use the ${chatApp?.name} ChatAible App at <a href=${url} target="_blank" rel="noreferrer">${url}</a>.
  </div>
  `;

  return message;
}

export function shareChatAppByEmail(chatAppId, email) {
  return (dispatch, getState) => {
    const chatApp = blueprintReducer.getBlueprint(getState(), chatAppId);

    if (!chatApp) {
      toast(`Oh no! Something went wrong. Please refresh the page and try again.`, {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3500
      });
      return Promise.resolve();
    }

    const folderId = chatApp?.folder_id;

    const user = {
      email: email
    };

    const url = getChatAppLink(chatApp);

    const uiState = {
      link: url,
      name: chatApp.name,
      id: chatAppId,
      type: "SHARE"
    };

    const params = [
      `chat-id=${encodeURIComponent(chatAppId)}`,
      `ui-state=${encodeURIComponent(btoa(JSON.stringify(uiState)))}`
    ];

    return dispatch(foldersReducer.addUserRole(folderId, user, CHATAPP_ROLE, params))
      .then((r) => {
        toast(`${chatApp?.name} has been shared with ${email}.`, {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3500
        });
        return r;
      })
      .catch((e) => {
        // toast(`${chatApp?.name} has been shared with ${email} and they've been invited to join ChatAible.`, {
        //   type: toast.TYPE.SUCCESS,
        //   position: toast.POSITION.TOP_CENTER,
        //   autoClose: 3500,
        // });
      });
  };
}

export function encodeUiState(uiState) {
  return encodeURIComponent(btoa(JSON.stringify(uiState)));
}

export function decodeUiState(uiStateParam) {
  let uiState;

  try {
    uiState = JSON.parse(atob(uiStateParam));
  } catch (e) {
    console.log(e);
  }

  return uiState;
}

export function shareChatAppByLink(chatAppId, email, password, accountId) {
  return (dispatch, getState) => {
    const chatApp = blueprintReducer.getBlueprint(getState(), chatAppId);

    if (!chatApp) {
      toast(`Oh no! Something went wrong. Please refresh the page and try again.`, {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3500
      });
      return Promise.resolve();
    }

    const chatAppArtifactId = chatApp?.wizard?.steps?.[0]?.artifactId;
    const chatAppDatasetId = chatApp?.wizard?.steps?.[0]?.datasetId;
    if (chatAppArtifactId) {
      const artifact = languageModelArtifactReducer.getCorpus(getState(), chatAppArtifactId);
      accountId = artifact?.account_id;
    } else if (chatAppDatasetId) {
      accountId = dataset?.account_id;
      const dataset = homeReducer.getDataset(getState(), chatAppDatasetId);
    }
    if (!accountId) {
      accountId = chatGPTReducer.getChatGPTAccountId(getState());
    }

    const presignedRequest = {
      expires_in: "7 days",
      method_override: "GET",
      action: "account.create_foreign_sts_token",
      params: {
        id: accountId
      }
    };

    const currTenant = loginReducer.getSelectedTenant(getState());

    return AuthAPI.postUser(email).then((r) => {
      return AuthAPI.postPassword(r?.token, password)
        .then((response) => {
          const tenant = response?.tenants?.find((t) => {
            return t?.id === currTenant?.id;
          });

          const tenantToken = tenant?.invite?.token;
          const userToken = response?.user_token;

          const headers = {
            Authorization: "Bearer " + tenantToken
          };

          return PresignedUrlAPI.generatePresignedUrl(presignedRequest, headers, true).then((presignedUrl) => {
            const url = getChatAppLink(chatApp);

            const uiState = {
              url: presignedUrl,
              method: "GET",
              link: url,
              name: chatApp.name,
              account_id: accountId,
              email: email,
              id: chatAppId,
              type: "SHARE_LINK"
            };
            const params = [`ui-state=${encodeUiState(uiState)}`];

            return url + constructQueryParams(params);
          });
        })
        .catch((e) => {
          console.log(e);
          throw e;
        });
    });
  };
}

export function updateAuthKey() {
  return (dispatch, getState) => {
    const uiState = globalReducer.getUiState(getState());

    const url = uiState?.url;
    const chatAppName = uiState?.name;
    const method = uiState?.method ?? "GET";

    return dispatch(executePresignedShare(url, undefined, chatAppName, method)).then((response) => {
      const rawAuthKey =
        response?.headers?.find((h) => {
          return h?.[1]?.includes("aible-sts");
        })?.[1] ?? response?.headers?.[1]?.[1];

      const authKey = rawAuthKey.substring("Bearer ".length);

      const authExpires = (Number(response?.expires_on_epoch) + 9 * 60 * 60 * 1000) * 1000;

      sessionStorage.setItem("aible_auth_token", authKey);
      sessionStorage.setItem("aible_token_expires", authExpires);
    });
  };
}

export function initShareChatApp(uiState) {
  return (dispatch, getState) => {
    if (uiState?.type === "SHARE_LINK") {
      dispatch(globalReducer.updateUiState(uiState));

      const handleRedirect = () => {
        const url = new URL(uiState?.link);
        const path = url?.pathname ?? `/next/new/new/${uiState?.id}`;
        const queryParams = `?ui-state=${encodeUiState(uiState)}`;
        dispatch(
          push({
            pathname: path,
            search: queryParams
          })
        );

        return Promise.resolve();
      };

      return dispatch(updateAuthKey(uiState))
        .then(() => {
          dispatch(loginReducer.identifyUserForThirdPartyPackages(uiState?.email));
          dispatch(loginReducer.updateIsAuthenticated(true));

          setInterval(
            () => {
              dispatch(updateAuthKey(uiState));
            },
            10 * 60 * 1000
          );

          return dispatch(accountsReducer.fetchAccount(uiState?.account_id)).then(() => {
            const account = accountsReducer.getAccount(getState(), uiState?.account_id);

            const tempTenant = {
              id: account?.tenant_id,
              invite: {
                token: getAPIToken(),
                status: "ACTIVE"
              },
              status: "ACTIVE"
            };
            dispatch(loginReducer.updateTenants([tempTenant], true));
            dispatch(loginReducer.updateSelectedTenant(tempTenant));
            dispatch(accountsReducer.updateSelectedAccount(account?.id));

            return dispatch(blueprintReducer.fetchBlueprint(uiState?.id)).then(() => {
              const chatApp = blueprintReducer.getBlueprint(getState(), uiState?.id);

              const chatAppArtifactId = chatApp?.wizard?.steps?.[0]?.artifactId;
              const chatAppDatasetId = chatApp?.wizard?.steps?.[0]?.datasetId;
              const chatAppTrainingDatasetId = chatApp?.wizard?.trainingDatasetId;

              let promises = [];
              if (chatAppArtifactId) {
                promises.push(dispatch(languageModelArtifactReducer.fetchCorpus(chatAppArtifactId)));
              }

              if (chatAppDatasetId) {
                promises.push(dispatch(homeReducer.fetchDataset(chatAppDatasetId)));
              }

              if (chatAppTrainingDatasetId) {
                promises.push(
                  dispatch(languageModelArtifactReducer.fetchArtifact("FINE_TUNING_DATA", chatAppTrainingDatasetId))
                );
              }

              return Promise.all(promises)
                .then(() => {
                  return handleRedirect();
                })
                .catch(() => {
                  return handleRedirect();
                });
            });
          });
        })
        .catch(() => {
          toast(`There was an error opening the chat app. Please try again.`, {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 5000
          });
          dispatch(
            push({
              pathname: "/login"
            })
          );
        });
    }
  };
}

export function executePresignedShare(url, payload, chatAppName, method) {
  return (dispatch, getState) => {
    if (!url) {
      console.log("No URL in uiState");
      return Promise.resolve();
    }
    return PresignedUrlAPI.executePresignedUrl(url, payload, method ?? "POST")
      .then((r) => {
        return r;
        // toast(`Welcome to the ${chatAppName ? chatAppName + " " : ""}Chat App!`, {
        //   type: toast.TYPE.SUCCESS,
        //   position: toast.POSITION.TOP_CENTER,
        //   autoClose: 5000,
        // });
      })
      .catch((e) => {
        console.log(e?.message);
        throw e?.message?.[0]?.key;
      });
  };
}

export function shareWithNewUser(email, uiState) {
  return (dispatch, getState) => {
    if (typeof uiState === "string") {
      try {
        uiState = JSON.parse(atob(uiState));
      } catch (e) {
        console.log(e);
      }
    }
    if (!uiState?.type === "SHARE") {
      return Promise.resolve();
    }

    const url = uiState?.url;

    const payload = {
      role_user_email: email,
      role_type: CHATAPP_ROLE
    };

    const params = [
      `chat-id=${encodeURIComponent(uiState?.id)}`,
      `ui-state=${encodeURIComponent(btoa(JSON.stringify(uiState)))}`
    ];

    return dispatch(executePresignedShare(url + "&" + params.join("&"), payload)).then((r) => {
      return r;
    });
  };
}

export function createAZTestApp(name, apps, distribution) {
  return (dispatch, getState) => {
    dispatch(updateIsCreatingApp(true));

    const folderId = foldersReducer.getSelectedFolder(getState());

    const currDate = new Date().toISOString();

    const currTest = {
      testApps: apps,
      appDistribution: distribution,
      dateStart: currDate
    };
    const retChatAppState = {
      isChatApp: true,
      isAZTestApp: true,
      name: name,
      azTests: [currTest]
    };

    return BlueprintAPI.createBlueprint(retChatAppState, folderId, retChatAppState?.name).then((r) => {
      console.log("what is r", r);
      dispatch(blueprintReducer.updateBlueprint(r.id, r));

      dispatch(updateIsCreatingApp(false));
    });
  };
}

export function updateAZTestApp(id, name, apps, distribution) {
  return (dispatch, getState) => {
    dispatch(updateIsCreatingApp(true));

    const chatApp = blueprintReducer.getBlueprint(getState(), id);
    const currApp = chatApp?.wizard;
    const currName = name ?? chatApp?.name ?? currApp.name;
    const currDate = new Date().toISOString();
    const currTest = {
      testApps: apps,
      appDistribution: distribution,
      dateStart: currDate
    };

    const azTests = currApp.azTests?.length ? [...currApp.azTests] : [];
    azTests[azTests.length - 1].dateEnd = currDate;
    azTests.push(currTest);
    const retWizard = {
      ...currApp,
      isChatApp: true,
      isAZTestApp: true,
      name: currName,
      azTests: azTests
    };

    return BlueprintAPI.updateBlueprint(id, retWizard, retWizard?.name).then((r) => {
      dispatch(blueprintReducer.updateBlueprint(r.id, r));

      dispatch(updateIsCreatingApp(false));
    });
  };
}

export function updateAZTestAppConcurrency(id, concurrencyLevel = "NONE") {
  return (dispatch, getState) => {
    dispatch(updateIsCreatingApp(true));

    const chatApp = blueprintReducer.getBlueprint(getState(), id);
    const currApp = chatApp?.wizard;

    const retWizard = {
      ...currApp,
      concurrencyLevel: concurrencyLevel
    };

    return BlueprintAPI.updateBlueprint(id, retWizard).then((r) => {
      dispatch(blueprintReducer.updateBlueprint(r.id, r));

      dispatch(updateIsCreatingApp(false));
    });
  };
}

export function initAZTestReporting() {
  return async (dispatch, getState) => {
    await dispatch(viewReducer.initChatAnalytics());
  };
}

export function getLineageChangeExternalName(state, type, id) {
  if (type === "DATASET") {
    const dataset = homeReducer.getDataset(state, id);
    return dataset?.label;
  }
  if (type === "DOCUMENT") {
    const document = languageModelArtifactReducer.getCorpus(state, id);
    return document?.name;
  }
  if (type === "BLUEPRINT") {
    const blueprint = blueprintReducer.getBlueprint(state, id);
    return blueprint?.name;
  }
  if (type === "CHAT_TONE") {
    const chatTone = chatToneReducer.getTone(state, id);
    return chatTone?.name;
  }
  if (type === "MODEL") {
    const modelOptions = chatGPTReducer.getModelOptions(state);
    const currOption = modelOptions?.find((o) => o.value === id);

    return currOption?.label;
  }
  if (type === "TAG") {
    const tag = tagsReducer.getTagById(state, id);
    return tag?.label;
  }
}

export function getLineageChanges(state, parentValues = {}, currValues = {}, changesList = CHATAPP_LINEAGE_CHANGES) {
  const lineageChanges = [];
  changesList.forEach((cChange) => {
    const values = cChange?.keys.map((k) => {
      const kValue = {
        key: k,
        parentValue: undefined,
        currValue: undefined,
        hasChanged: false
      };
      // name fetching is same for string or object diving so we're combining the logic here
      if (typeof k === "string" || k?.type === "object") {
        if (typeof k === "string") {
          kValue.parentValue = parentValues[k];
          kValue.currValue = currValues[k];
          kValue.hasChanged = kValue.parentValue !== kValue.currValue;
        } else if (k?.type === "object") {
          kValue.parentValue = parentValues[k.key];
          kValue.currValue = currValues[k.key];
          k.subKeys.forEach((cKey) => {
            kValue.parentValue = kValue.parentValue?.[cKey];
            kValue.currValue = kValue.currValue?.[cKey];
          });
          kValue.hasChanged = kValue.parentValue !== kValue.currValue;
        }
        // name fetching is same for string or object diving
        if (cChange.type !== undefined) {
          const pLabel =
            cChange.type === "VALUE"
              ? kValue.parentValue
              : getLineageChangeExternalName(state, cChange.type, kValue.parentValue);
          const cLabel =
            cChange.type === "VALUE"
              ? kValue.currValue
              : getLineageChangeExternalName(state, cChange.type, kValue.currValue);
          if (pLabel !== undefined && cLabel !== undefined) {
            kValue.parentLabel = pLabel;
            kValue.currLabel = cLabel;
          }
        }
      } else if (k?.type === "array") {
        const pArray = parentValues[k.key] ?? [];
        const cArray = currValues[k.key] ?? [];

        kValue.parentValue = [...pArray].sort();
        kValue.currValue = [...cArray].sort();

        kValue.hasChanged = JSON.stringify(kValue.parentValue) !== JSON.stringify(kValue.currValue);

        if (cChange?.type !== undefined) {
          const pLabels = kValue.parentValue.map((pId) =>
            cChange.type === "VALUE" ? pId : getLineageChangeExternalName(state, cChange.type, pId)
          );
          const cLabels = kValue.currValue.map((cId) =>
            cChange.type === "VALUE" ? cId : getLineageChangeExternalName(state, cChange.type, cId)
          );

          if (!pLabels.includes(undefined) && !cLabels.includes(undefined)) {
            kValue.parentLabel = pLabels.join(", ");
            kValue.currLabel = cLabels.join(", ");
          }
        }
      }
      return kValue;
    });

    const changes = values?.filter((v) => v.hasChanged);
    if (changes.length) {
      let retChanges = changes;
      if (cChange.value === "TARGET_DATASET") {
        // Don't duplicate the dataset change for target dataset
        retChanges = [changes[0]];
      }
      const currChange = {
        value: cChange.value,
        label: cChange.label,
        changes: retChanges
      };
      lineageChanges.push(currChange);
    }
  });

  return lineageChanges;
}

export function getLineage(state, blueprintIdOverride) {
  const chatApp = chatGPTReducer.getChatApp(state);
  const blueprintId = blueprintIdOverride ?? chatApp?.id;

  const blueprint = blueprintReducer.getBlueprint(state, blueprintId);

  const rawLineage = blueprint?.wizard?.lineage ?? [];

  let previousBp;
  const lineage = rawLineage?.map((lineageItem) => {
    const lineageBp = blueprintReducer.getBlueprint(state, lineageItem?.id);
    const lineageBpValues = lineageBp?.wizard ?? {};
    let changes;
    if (previousBp) {
      const previousBpValues = previousBp?.wizard ?? {};
      changes = getLineageChanges(state, previousBpValues, lineageBpValues, CHATAPP_LINEAGE_CHANGES);
    }
    previousBp = lineageBp;
    return {
      ...lineageItem,
      ...lineageBp,
      lineageChanges: changes,
      isUpdated: lineageItem?.updated_date !== lineageBp?.updated_date
    };
  });

  const currLineageItem = {
    ...blueprint
  };
  if (previousBp) {
    const previousBpValues = previousBp?.wizard ?? {};
    const blueprintValues = blueprint?.wizard ?? {};
    currLineageItem.lineageChanges = getLineageChanges(
      state,
      previousBpValues,
      blueprintValues,
      CHATAPP_LINEAGE_CHANGES
    );
  }

  lineage.push(currLineageItem);

  return lineage;
}

export function saveChatApp(
  chatAppState,
  blueprintId = undefined,
  nextIdOverride = undefined,
  skipInit = false,
  showToast = false,
  saveLineage = true,
  saveToTemplates = false,
  skipRedirect = false,
  isKPIIdentical = false
) {
  return async (dispatch, getState) => {
    const folderId = foldersReducer.getSelectedFolder(getState());
    const nextId = nextIdOverride ?? nextReducer.getCurrentNextId(getState());
    let datasetId = nextReducer.getDatasetId(getState(), nextId);
    const dataset = homeReducer.getDataset(getState(), datasetId);
    const artifactId = chatGPTReducer.getArtifactId(getState());
    const projectId = chatGPTReducer.getProjectId(getState());
    const modelId = chatGPTReducer.getModelId(getState());
    const secondaryModelId = chatGPTReducer.getSecondaryModel(getState());

    const addTrainingDataset = getAddTrainingDataset(getState());
    const currentArtifact = nextReducer.getCurrentArtifact(getState(), nextId);

    const chatRestrictChartsToPinned = chatGPTReducer.getChatRestrictChartsToPinned(getState());
    const chatAppSaveFewShot = chatGPTReducer.getChatAppSaveFewShot(getState());

    const addToAzTest = getAddToAzTest(getState());
    const targetAzTest = getCurrentAZTest(getState(), addToAzTest);
    const appAZDistribution = getAppAZDistribution(getState());
    const appAZAllocation = getAppAZAllocation(getState());

    const datasetColumnDetails = chatGPTReducer.getDatasetColumnDetails(getState());

    const hideOutcomeSelection = getHideOutcomeSelection(getState());

    if (chatAppState.templateType === "WHATS_CHANGED" && !chatAppState.isKPIAnalysis && !isKPIIdentical) {
      const exclude_columns = chatGPTReducer
        .getKPIDeepDiveExcludeColumns(getState(), datasetId)
        .filter((item) => item !== dataset.target);

      const relationBuilder = builderFromDataset(dataset).exclude(exclude_columns).build();

      let newDataset;
      try {
        newDataset = await dispatch(
          relationReducer.copyAndTransform(datasetId, `${dataset.name}_deep_dive_${dataset.target}`, relationBuilder)
        );
        if (!newDataset) {
          throw new Error("There was an error creating the dataset");
        }

        while (newDataset.status_name !== "DONE") {
          await delay(3000);
          newDataset = await dispatch(homeReducer.fetchDataset(newDataset.id, true));
          dispatch(homeReducer.updateDataset(newDataset.id, newDataset));
        }
      } catch (e) {
        console.log(e);
        throw e;
      }

      datasetId = newDataset.id;
    }

    const retChatAppState = {
      ...chatAppState,
      chatTone: chatGPTReducer.getChatTone(getState()),
      datasetId: datasetId,
      projectId: projectId,
      modelId: modelId,
      secondaryModelId: secondaryModelId,
      steps: getSteps(getState()),
      useSecurityMasking: chatGPTReducer.getUseSecurityMasking(getState()),
      showRequestJSON: chatGPTReducer.getShowRequestJSON(getState()),
      chatHistoryCount: chatGPTReducer.getChatHistoryCount(getState()),
      showTokens: chatGPTReducer.getShowTokens(getState()),
      useMaxTokens: chatGPTReducer.getUseMaxTokens(getState()),
      maxTokens: chatGPTReducer.getMaxTokens(getState()),
      retrievalK: chatGPTReducer.getRetrievalK(getState()),
      totalChartLimit: chatGPTReducer.getTotalChartLimit(getState()),
      temperature: chatGPTReducer.getTemperature(getState()),
      frequencyPenalty: chatGPTReducer.getFrequencyPenalty(getState()),
      isChatApp: true,
      isBenchmark: chatGPTReducer.getIsBenchmark(getState()),
      allowedAppUseCases: getAllowedAppUseCases(getState()),
      allowedAppDatasets: getAllowedAppDatasets(getState()),
      allowedAppDocuments: getAllowedAppDocuments(getState()),
      artifactId: artifactId,
      startInDashboard: getStartInDashboard(getState()),
      addTrainingDataset: getAddTrainingDataset(getState()),
      chatRestrictChartsToPinned: chatRestrictChartsToPinned,
      retrieverSearchType: chatGPTReducer.getRetrieverSearchType(getState()),
      detectValuesLLM: chatGPTReducer.getDetectValuesLLM(getState()),
      includeChartCounts: chatGPTReducer.getIncludeChartCounts(getState()),
      chatAppSaveFewShot: chatAppSaveFewShot,
      hideOutcomeSelection: hideOutcomeSelection,
      datasetColumnDetails: datasetColumnDetails,
      fewShotExamples: nextReducer.getFewShotExamples(getState(), nextReducer.getCurrentNextId(getState())),
      appInitSummarize: chatGPTReducer.getAppInitSummarize(getState()),
      appInitAutoAsk: chatGPTReducer.getAppInitAutoAsk(getState()),
      savedComparisonOptions: nextReducer.getComparisonOptions(getState(), nextId),
      chatFlow: chatGPTReducer.getChatFlow(getState())
    };

    if (chatRestrictChartsToPinned || chatAppSaveFewShot) {
      retChatAppState.savedNextId = nextId;
    }

    if (addTrainingDataset === true) {
      retChatAppState.trainingDatasetId = currentArtifact?.id;
    }

    if (saveToTemplates) {
      retChatAppState.isSavedWork = true;
    }

    console.log("create chat app ", retChatAppState, blueprintId);

    const steps = getSteps(getState());

    // Logic to check if prompts, automatic/suggested questions have been modified from the base blueprint
    // and will create a new use case for the agent to use.
    const targetBlueprintId = steps?.[0]?.chatBlueprintId;
    const targetBlueprint = blueprintReducer.getBlueprint(getState(), targetBlueprintId);
    const targetChatGpt = targetBlueprint?.wizard?.chatGPT;
    const targetUseCaseSettings = targetChatGpt?.useCaseSettings;

    const useCaseSettings = chatGPTReducer.getUseCaseSettings(getState());
    const modelUseCaseSettings = useCaseSettings?.[modelId];

    const secondaryLlmPrompt = chatGPTReducer.getSecondaryPrompt(getState());
    const targetSecondaryLlmPrompt = targetChatGpt?.secondaryLlmSettings?.prompt;

    const replayPrompts = chatGPTReducer.getReplayPrompts(getState());
    const targetReplayPrompts = targetChatGpt?.replayPrompts;

    if (modelUseCaseSettings || secondaryLlmPrompt || replayPrompts?.length > 0) {
      let hasModifiedUseCaseSettings = false;
      let hasModifiedSecondaryPrompt = false;
      let hasModifiedReplayPrompts = false;
      if (modelUseCaseSettings) {
        let comparisonModelId = modelId;
        const isInternModel = chatGPTReducer.getIsInternModel(getState(), modelId);
        if (isNaN(modelId) && isInternModel) {
          comparisonModelId = AIBLE_INTERN_LABEL_PREFIX;
        }
        hasModifiedUseCaseSettings = Object.keys(modelUseCaseSettings)?.some((settingKey) => {
          const hasSetting = Boolean(
            modelUseCaseSettings[settingKey] !== undefined && modelUseCaseSettings[settingKey]?.length
          );
          const modifiedSetting =
            modelUseCaseSettings[settingKey] !== targetUseCaseSettings?.[comparisonModelId]?.[settingKey];
          if (hasSetting && modifiedSetting) {
            return true;
          }
          return false;
        });
      }

      if (secondaryLlmPrompt) {
        hasModifiedSecondaryPrompt = secondaryLlmPrompt !== targetSecondaryLlmPrompt;
      }

      if (replayPrompts.length > 0) {
        if (replayPrompts.length !== targetReplayPrompts?.length) {
          hasModifiedReplayPrompts = true;
        } else {
          hasModifiedReplayPrompts = replayPrompts.some((promptObj) => {
            const foundPrompt = targetReplayPrompts?.find((targetPrompt) => targetPrompt?.prompt === promptObj?.prompt);
            const matchingKeyLength =
              foundPrompt && Object.keys(promptObj)?.length === Object.keys(foundPrompt)?.length;

            if (!matchingKeyLength) {
              return true;
            }

            for (const key in foundPrompt) {
              if (foundPrompt[key] !== promptObj[key]) {
                return true;
              }
            }

            return false;
          });
        }
      }

      if (hasModifiedUseCaseSettings || hasModifiedSecondaryPrompt || hasModifiedReplayPrompts) {
        const newUsecaseId = await dispatch(
          chatGPTReducer.saveBlueprint(
            `${chatAppState?.name} (Custom Agent)`,
            chatAppState?.description,
            undefined,
            undefined,
            true
          )
        );
        retChatAppState.steps = {
          ...retChatAppState?.steps,
          [0]: {
            ...retChatAppState?.steps?.[0],
            chatBlueprintId: newUsecaseId
          }
        };
      }
    }

    dispatch(updateIsCreatingApp(true));
    dispatch(nextReducer.updateReportLabelAndContentGenerationCollection(nextId, retChatAppState?.name));

    if (blueprintId !== undefined) {
      return BlueprintAPI.updateBlueprint(blueprintId, retChatAppState, retChatAppState?.name).then((r) => {
        console.log("what is r", r);
        dispatch(blueprintReducer.updateBlueprint(r.id, r));
        dispatch(chatGPTReducer.initChatApp(r, skipRedirect));

        if (addToAzTest && !targetAzTest?.testApps?.includes(r?.id)) {
          const retApps = [...(targetAzTest?.testApps || {}), r?.id];

          const retDistribution = {
            ...appAZDistribution,
            [r?.id]: appAZAllocation
          };
          dispatch(updateAZTestApp(addToAzTest, undefined, retApps, retDistribution));
        }
        dispatch(updateIsCreatingApp(false));

        return r;
      });
    } else {
      let currLineage = [];
      if (saveLineage) {
        const parentChatApp = chatGPTReducer.getChatApp(getState());
        if (parentChatApp?.id) {
          const chatAppLineage = parentChatApp?.wizard?.lineage ?? [];

          const currLineageItem = {};
          const KEYS_TO_SAVE = ["created_by", "created_date", "updated_date", "id", "name"];
          KEYS_TO_SAVE.forEach((k) => {
            currLineageItem[k] = parentChatApp[k];
          });
          currLineage = [...chatAppLineage, currLineageItem];
        }
      }
      retChatAppState.lineage = currLineage;
      return BlueprintAPI.createBlueprint(retChatAppState, folderId, retChatAppState?.name).then((r) => {
        console.log("what is r", r);
        dispatch(blueprintReducer.updateBlueprint(r.id, r));

        if (!skipInit) {
          dispatch(chatGPTReducer.initChatApp(r, skipRedirect)).then(() => {
            dispatch(chatGPTReducer.initChatAppHelper(r?.id));
          });
        }

        //dispatch(chatGPTReducer.initChatAppHelper(r.id));

        if (addToAzTest && !targetAzTest?.testApps?.includes(r?.id)) {
          const retApps = [...(targetAzTest?.testApps ?? []), r?.id];

          const retDistribution = {
            ...appAZDistribution,
            [r?.id]: appAZAllocation
          };
          dispatch(updateAZTestApp(addToAzTest, undefined, retApps, retDistribution));
        }
        if (showToast) {
          toast("Your new agent was created!", {
            type: toast.TYPE.SUCCESS,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          });
        }
        dispatch(updateIsCreatingApp(false));

        return r;
      });
    }
  };
}

export default chatAppReducer;
